// src/screens/DealerCalendarScreen.js
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { dealerAvailabilityAPI } from '../services/api';
import AppHeader from '../components/AppHeader';
import { Calendar, Clock, Plus, Trash2, ChevronLeft, ChevronRight, Settings, Ban, Save, RefreshCw } from 'lucide-react';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DealerCalendarScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Main state
  const [activeSection, setActiveSection] = useState('calendar'); // 'calendar', 'schedule', 'blackout', 'settings'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Calendar view state
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);

  // Weekly schedule rules state
  const [rules, setRules] = useState([]);
  const [editingRules, setEditingRules] = useState([]);

  // Settings state
  const [appointmentDuration, setAppointmentDuration] = useState(60);
  const [defaultCapacity, setDefaultCapacity] = useState(1);

  // New blackout date form
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  const [newBlackoutReason, setNewBlackoutReason] = useState('');

  // Generate slots modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateFrom, setGenerateFrom] = useState('');
  const [generateTo, setGenerateTo] = useState('');

  // Authorization check
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'dealer_admin') {
      navigate('/dealer');
      return;
    }
  }, [user, navigate]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Calculate date range for slots based on view
      const startOfView = getStartOfView(currentDate, viewMode);
      const endOfView = getEndOfView(currentDate, viewMode);

      const [slotsRes, rulesRes, blackoutRes, settingsRes] = await Promise.all([
        dealerAvailabilityAPI.list({
          from: formatDateForAPI(startOfView),
          to: formatDateForAPI(endOfView),
        }),
        dealerAvailabilityAPI.rulesGet(),
        dealerAvailabilityAPI.getBlackoutDates(),
        dealerAvailabilityAPI.getSettings(),
      ]);

      setSlots(slotsRes.data || []);
      setRules(rulesRes.data || []);
      setEditingRules(rulesRes.data || []);
      setBlackoutDates(blackoutRes.data || []);
      setAppointmentDuration(settingsRes.data?.appointmentDuration || 60);

      // Extract default capacity from rules if any
      if (rulesRes.data?.length > 0) {
        setDefaultCapacity(rulesRes.data[0].capacity || 1);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
      setError(err.response?.data?.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    if (user?.role === 'dealer_admin') {
      fetchData();
    }
  }, [user, fetchData]);

  // Helper functions
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getStartOfView = (date, mode) => {
    const d = new Date(date);
    if (mode === 'week') {
      const day = d.getDay();
      d.setDate(d.getDate() - day);
    } else {
      d.setDate(1);
    }
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getEndOfView = (date, mode) => {
    const d = new Date(date);
    if (mode === 'week') {
      const day = d.getDay();
      d.setDate(d.getDate() + (6 - day));
    } else {
      d.setMonth(d.getMonth() + 1);
      d.setDate(0);
    }
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const navigateView = (direction) => {
    const d = new Date(currentDate);
    if (viewMode === 'week') {
      d.setDate(d.getDate() + (direction * 7));
    } else {
      d.setMonth(d.getMonth() + direction);
    }
    setCurrentDate(d);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get dates for current view
  const viewDates = useMemo(() => {
    const dates = [];
    const start = getStartOfView(currentDate, viewMode);
    const end = getEndOfView(currentDate, viewMode);

    // For month view, include days from previous/next month to fill the grid
    if (viewMode === 'month') {
      const firstDay = start.getDay();
      start.setDate(start.getDate() - firstDay);
    }

    const d = new Date(start);
    const limit = viewMode === 'week' ? 7 : 42;

    for (let i = 0; i < limit; i++) {
      dates.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }

    return dates;
  }, [currentDate, viewMode]);

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const map = {};
    slots.forEach((slot) => {
      const dateKey = new Date(slot.date).toDateString();
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(slot);
    });
    // Sort slots within each day
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => a.time.localeCompare(b.time));
    });
    return map;
  }, [slots]);

  // Check if date is blacked out
  const isBlackoutDate = useCallback((date) => {
    const dateStr = date.toDateString();
    return blackoutDates.some((b) => new Date(b.date).toDateString() === dateStr);
  }, [blackoutDates]);

  // Check if date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Get the current month for display
  const viewTitle = useMemo(() => {
    if (viewMode === 'week') {
      const start = getStartOfView(currentDate, 'week');
      const end = getEndOfView(currentDate, 'week');
      const startMonth = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startMonth} - ${endMonth}`;
    }
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentDate, viewMode]);

  // Save weekly schedule rules
  const handleSaveRules = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate rules
      for (const rule of editingRules) {
        if (!rule.startTime || !rule.endTime) {
          setError('Start time and end time are required for all rules');
          return;
        }
        if (rule.startTime >= rule.endTime) {
          setError('End time must be after start time');
          return;
        }
      }

      await dealerAvailabilityAPI.rulesSave({ rules: editingRules });
      setRules(editingRules);
      setSuccessMessage('Weekly schedule saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Save rules error:', err);
      setError(err.response?.data?.message || 'Failed to save weekly schedule');
    } finally {
      setSaving(false);
    }
  };

  // Add new rule
  const addRule = (weekday) => {
    setEditingRules([
      ...editingRules,
      {
        weekday,
        startTime: '09:00',
        endTime: '17:00',
        intervalMinutes: appointmentDuration,
        capacity: defaultCapacity,
      },
    ]);
  };

  // Update rule
  const updateRule = (index, field, value) => {
    const newRules = [...editingRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setEditingRules(newRules);
  };

  // Remove rule
  const removeRule = (index) => {
    setEditingRules(editingRules.filter((_, i) => i !== index));
  };

  // Add blackout date
  const handleAddBlackoutDate = async () => {
    if (!newBlackoutDate) {
      setError('Please select a date');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await dealerAvailabilityAPI.addBlackoutDate({
        date: newBlackoutDate,
        reason: newBlackoutReason,
      });
      await fetchData();
      setNewBlackoutDate('');
      setNewBlackoutReason('');
      setSuccessMessage('Blackout date added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Add blackout date error:', err);
      setError(err.response?.data?.message || 'Failed to add blackout date');
    } finally {
      setSaving(false);
    }
  };

  // Remove blackout date
  const handleRemoveBlackoutDate = async (id) => {
    try {
      setSaving(true);
      await dealerAvailabilityAPI.removeBlackoutDate(id);
      await fetchData();
    } catch (err) {
      console.error('Remove blackout date error:', err);
      setError(err.response?.data?.message || 'Failed to remove blackout date');
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      await dealerAvailabilityAPI.updateSettings({ appointmentDuration });
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Save settings error:', err);
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Generate slots from rules
  const handleGenerateSlots = async () => {
    if (!generateFrom || !generateTo) {
      setError('Please select start and end dates');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const result = await dealerAvailabilityAPI.generate({
        from: generateFrom,
        to: generateTo,
        useRules: true,
      });
      await fetchData();
      setShowGenerateModal(false);
      setGenerateFrom('');
      setGenerateTo('');
      setSuccessMessage(`Generated ${result.data.created} slots successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Generate slots error:', err);
      setError(err.response?.data?.message || 'Failed to generate slots');
    } finally {
      setSaving(false);
    }
  };

  // Delete slot
  const handleDeleteSlot = async (slotId) => {
    try {
      await dealerAvailabilityAPI.remove(slotId);
      await fetchData();
    } catch (err) {
      console.error('Delete slot error:', err);
      setError(err.response?.data?.message || 'Failed to delete slot');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Appointment Calendar" subtitle="Manage your availability" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-app-text-secondary-light dark:text-app-text-secondary">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <AppHeader
        title="Appointment Calendar"
        subtitle="Manage your availability and scheduling"
      />

      <div className="flex-1 overflow-y-auto p-4 lg:p-6 lg:pb-6 pb-20 space-y-4 text-xs max-w-7xl lg:mx-auto w-full">
        {/* Messages */}
        {error && (
          <div className="bg-red-900/30 text-red-300 px-3 py-2 rounded-xl">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-400 hover:text-red-300">
              Dismiss
            </button>
          </div>
        )}
        {successMessage && (
          <div className="bg-green-900/30 text-green-300 px-3 py-2 rounded-xl">
            {successMessage}
          </div>
        )}

        {/* Section Navigation */}
        <div className="flex gap-2 border-b border-app-border-light dark:border-app-border overflow-x-auto">
          <button
            onClick={() => setActiveSection('calendar')}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 ${
              activeSection === 'calendar'
                ? 'border-app-accent text-app-accent'
                : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
            }`}
          >
            <Calendar size={16} />
            Calendar View
          </button>
          <button
            onClick={() => setActiveSection('schedule')}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 ${
              activeSection === 'schedule'
                ? 'border-app-accent text-app-accent'
                : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
            }`}
          >
            <Clock size={16} />
            Weekly Schedule
          </button>
          <button
            onClick={() => setActiveSection('blackout')}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 ${
              activeSection === 'blackout'
                ? 'border-app-accent text-app-accent'
                : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
            }`}
          >
            <Ban size={16} />
            Blackout Dates
          </button>
          <button
            onClick={() => setActiveSection('settings')}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 ${
              activeSection === 'settings'
                ? 'border-app-accent text-app-accent'
                : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
            }`}
          >
            <Settings size={16} />
            Settings
          </button>
        </div>

        {/* Calendar View Section */}
        {activeSection === 'calendar' && (
          <div className="space-y-4">
            {/* Calendar Controls */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigateView(-1)}
                    className="p-2 rounded-lg bg-app-bg-light dark:bg-app-bg hover:bg-app-input-light dark:hover:bg-app-input border border-app-border-light dark:border-app-border"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={goToToday}
                    className="px-3 py-2 rounded-lg bg-app-bg-light dark:bg-app-bg hover:bg-app-input-light dark:hover:bg-app-input border border-app-border-light dark:border-app-border text-sm"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => navigateView(1)}
                    className="p-2 rounded-lg bg-app-bg-light dark:bg-app-bg hover:bg-app-input-light dark:hover:bg-app-input border border-app-border-light dark:border-app-border"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <span className="text-base font-semibold text-app-text-light dark:text-app-text ml-2">
                    {viewTitle}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex rounded-lg border border-app-border-light dark:border-app-border overflow-hidden">
                    <button
                      onClick={() => setViewMode('week')}
                      className={`px-3 py-2 text-sm ${
                        viewMode === 'week'
                          ? 'bg-app-accent text-black'
                          : 'bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text hover:bg-app-input-light dark:hover:bg-app-input'
                      }`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setViewMode('month')}
                      className={`px-3 py-2 text-sm ${
                        viewMode === 'month'
                          ? 'bg-app-accent text-black'
                          : 'bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text hover:bg-app-input-light dark:hover:bg-app-input'
                      }`}
                    >
                      Month
                    </button>
                  </div>

                  <button
                    onClick={() => setShowGenerateModal(true)}
                    className="px-3 py-2 rounded-lg bg-app-accent text-black font-semibold text-sm flex items-center gap-2"
                  >
                    <RefreshCw size={16} />
                    Generate Slots
                  </button>
                </div>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border overflow-hidden">
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-app-border-light dark:border-app-border">
                {DAYS_SHORT.map((day) => (
                  <div
                    key={day}
                    className="p-2 text-center text-xs font-semibold text-app-text-secondary-light dark:text-app-text-secondary bg-app-bg-light dark:bg-app-bg"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar cells */}
              <div className={`grid grid-cols-7 ${viewMode === 'month' ? 'auto-rows-fr' : ''}`}>
                {viewDates.map((date, index) => {
                  const dateKey = date.toDateString();
                  const daySlots = slotsByDate[dateKey] || [];
                  const isBlackout = isBlackoutDate(date);
                  const isPast = isPastDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isCurrentMonth = date.getMonth() === currentDate.getMonth();

                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] p-2 border-b border-r border-app-border-light dark:border-app-border ${
                        isBlackout
                          ? 'bg-red-900/20'
                          : isPast
                          ? 'bg-app-bg-light/50 dark:bg-app-bg/50'
                          : 'bg-app-bg-light dark:bg-app-bg'
                      } ${!isCurrentMonth && viewMode === 'month' ? 'opacity-40' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`text-xs font-semibold ${
                            isToday
                              ? 'bg-app-accent text-black px-1.5 py-0.5 rounded-full'
                              : 'text-app-text-light dark:text-app-text'
                          }`}
                        >
                          {date.getDate()}
                        </span>
                        {isBlackout && (
                          <span className="text-[10px] text-red-400 font-medium">Blocked</span>
                        )}
                      </div>

                      {/* Slots for this day */}
                      {!isBlackout && (
                        <div className="space-y-1 max-h-[80px] overflow-y-auto">
                          {daySlots.length > 0 ? (
                            viewMode === 'week' ? (
                              daySlots.map((slot) => (
                                <div
                                  key={slot._id}
                                  className="flex items-center justify-between text-[10px] bg-app-accent/20 text-app-accent px-1.5 py-0.5 rounded group"
                                >
                                  <span>
                                    {slot.time} ({slot.bookedCount}/{slot.capacity})
                                  </span>
                                  {!isPast && (
                                    <button
                                      onClick={() => handleDeleteSlot(slot._id)}
                                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 ml-1"
                                    >
                                      <Trash2 size={10} />
                                    </button>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="text-[10px] text-app-accent">
                                {daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}
                              </div>
                            )
                          ) : (
                            !isPast && (
                              <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">
                                No slots
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-app-accent/20"></div>
                <span>Available Slots</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-900/20"></div>
                <span>Blackout Date</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-app-bg-light/50 dark:bg-app-bg/50 border border-app-border-light dark:border-app-border"></div>
                <span>Past Date</span>
              </div>
            </div>
          </div>
        )}

        {/* Weekly Schedule Section */}
        {activeSection === 'schedule' && (
          <div className="space-y-4">
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-app-text-light dark:text-app-text">
                    Weekly Schedule Template
                  </h3>
                  <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
                    Define your recurring availability for each day of the week. This template is used when generating slots.
                  </p>
                </div>
                <button
                  onClick={handleSaveRules}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-app-accent text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Schedule'}
                </button>
              </div>

              <div className="space-y-3">
                {DAYS_OF_WEEK.map((day, weekday) => {
                  const dayRules = editingRules.filter((r) => r.weekday === weekday);

                  return (
                    <div
                      key={weekday}
                      className="bg-app-bg-light dark:bg-app-bg rounded-xl p-3 border border-app-border-light dark:border-app-border"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-app-text-light dark:text-app-text">
                          {day}
                        </span>
                        <button
                          onClick={() => addRule(weekday)}
                          className="text-xs text-app-accent hover:text-app-accent-hover flex items-center gap-1"
                        >
                          <Plus size={14} />
                          Add Time Block
                        </button>
                      </div>

                      {dayRules.length === 0 ? (
                        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                          Closed - No availability set
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {dayRules.map((rule, idx) => {
                            const ruleIndex = editingRules.findIndex(
                              (r) => r === rule
                            );

                            return (
                              <div
                                key={idx}
                                className="flex flex-wrap items-center gap-2 text-xs"
                              >
                                <div className="flex items-center gap-1">
                                  <label className="text-app-text-secondary-light dark:text-app-text-secondary">
                                    From:
                                  </label>
                                  <input
                                    type="time"
                                    value={rule.startTime}
                                    onChange={(e) =>
                                      updateRule(ruleIndex, 'startTime', e.target.value)
                                    }
                                    className="px-2 py-1 rounded bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <label className="text-app-text-secondary-light dark:text-app-text-secondary">
                                    To:
                                  </label>
                                  <input
                                    type="time"
                                    value={rule.endTime}
                                    onChange={(e) =>
                                      updateRule(ruleIndex, 'endTime', e.target.value)
                                    }
                                    className="px-2 py-1 rounded bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <label className="text-app-text-secondary-light dark:text-app-text-secondary">
                                    Interval:
                                  </label>
                                  <select
                                    value={rule.intervalMinutes}
                                    onChange={(e) =>
                                      updateRule(ruleIndex, 'intervalMinutes', parseInt(e.target.value))
                                    }
                                    className="px-2 py-1 rounded bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                                  >
                                    <option value={15}>15 min</option>
                                    <option value={30}>30 min</option>
                                    <option value={45}>45 min</option>
                                    <option value={60}>1 hour</option>
                                    <option value={90}>1.5 hours</option>
                                    <option value={120}>2 hours</option>
                                  </select>
                                </div>
                                <div className="flex items-center gap-1">
                                  <label className="text-app-text-secondary-light dark:text-app-text-secondary">
                                    Capacity:
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={99}
                                    value={rule.capacity}
                                    onChange={(e) =>
                                      updateRule(ruleIndex, 'capacity', parseInt(e.target.value) || 1)
                                    }
                                    className="w-14 px-2 py-1 rounded bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                                  />
                                </div>
                                <button
                                  onClick={() => removeRule(ruleIndex)}
                                  className="text-red-400 hover:text-red-300 p-1"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Blackout Dates Section */}
        {activeSection === 'blackout' && (
          <div className="space-y-4">
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
              <h3 className="text-base font-semibold text-app-text-light dark:text-app-text mb-2">
                Add Blackout Date
              </h3>
              <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-4">
                Block specific dates from accepting appointments (holidays, maintenance, etc.)
              </p>

              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary block mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={newBlackoutDate}
                    onChange={(e) => setNewBlackoutDate(e.target.value)}
                    min={formatDateForAPI(new Date())}
                    className="px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary block mb-1">
                    Reason (Optional)
                  </label>
                  <input
                    type="text"
                    value={newBlackoutReason}
                    onChange={(e) => setNewBlackoutReason(e.target.value)}
                    placeholder="e.g., Holiday, Staff Meeting"
                    className="w-full px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                  />
                </div>
                <button
                  onClick={handleAddBlackoutDate}
                  disabled={saving || !newBlackoutDate}
                  className="px-4 py-2 rounded-lg bg-app-accent text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  <Plus size={16} />
                  {saving ? 'Adding...' : 'Add Blackout'}
                </button>
              </div>
            </div>

            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
              <h3 className="text-base font-semibold text-app-text-light dark:text-app-text mb-4">
                Scheduled Blackout Dates
              </h3>

              {blackoutDates.length === 0 ? (
                <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                  No blackout dates scheduled.
                </p>
              ) : (
                <div className="space-y-2">
                  {blackoutDates
                    .filter((b) => new Date(b.date) >= new Date(new Date().setHours(0, 0, 0, 0)))
                    .map((blackout) => (
                      <div
                        key={blackout._id}
                        className="flex items-center justify-between bg-app-bg-light dark:bg-app-bg rounded-xl p-3 border border-app-border-light dark:border-app-border"
                      >
                        <div>
                          <span className="text-sm font-semibold text-app-text-light dark:text-app-text">
                            {new Date(blackout.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </span>
                          {blackout.reason && (
                            <span className="text-xs text-app-text-secondary-light dark:text-app-text-secondary ml-2">
                              - {blackout.reason}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveBlackoutDate(blackout._id)}
                          disabled={saving}
                          className="text-red-400 hover:text-red-300 p-2 disabled:opacity-50"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Section */}
        {activeSection === 'settings' && (
          <div className="space-y-4">
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
              <h3 className="text-base font-semibold text-app-text-light dark:text-app-text mb-2">
                Appointment Settings
              </h3>
              <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-4">
                Configure default settings for appointments
              </p>

              <div className="space-y-4">
                <div>
                  <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary block mb-2">
                    Default Appointment Duration
                  </label>
                  <select
                    value={appointmentDuration}
                    onChange={(e) => setAppointmentDuration(parseInt(e.target.value))}
                    className="w-full lg:w-64 px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1 hour 30 minutes</option>
                    <option value={120}>2 hours</option>
                    <option value={180}>3 hours</option>
                    <option value={240}>4 hours</option>
                  </select>
                  <p className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mt-1">
                    This determines how much time is allocated for each appointment
                  </p>
                </div>

                <div>
                  <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary block mb-2">
                    Default Slot Capacity
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={defaultCapacity}
                    onChange={(e) => setDefaultCapacity(parseInt(e.target.value) || 1)}
                    className="w-full lg:w-64 px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                  />
                  <p className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mt-1">
                    Maximum number of appointments that can be booked per time slot
                  </p>
                </div>

                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-app-accent text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Generate Slots Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-6 max-w-md w-full border border-app-border-light dark:border-app-border">
            <h3 className="text-lg font-semibold text-app-text-light dark:text-app-text mb-2">
              Generate Appointment Slots
            </h3>
            <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-4">
              Automatically create time slots based on your weekly schedule template.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary block mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={generateFrom}
                  onChange={(e) => setGenerateFrom(e.target.value)}
                  min={formatDateForAPI(new Date())}
                  className="w-full px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                />
              </div>
              <div>
                <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary block mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={generateTo}
                  onChange={(e) => setGenerateTo(e.target.value)}
                  min={generateFrom || formatDateForAPI(new Date())}
                  className="w-full px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text"
                />
              </div>

              {rules.length === 0 && (
                <div className="bg-yellow-900/30 text-yellow-300 px-3 py-2 rounded-lg text-xs">
                  No weekly schedule configured. Please set up your weekly schedule first.
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowGenerateModal(false);
                    setGenerateFrom('');
                    setGenerateTo('');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-app-bg-light dark:bg-app-bg border border-app-border-light dark:border-app-border text-app-text-light dark:text-app-text font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateSlots}
                  disabled={saving || !generateFrom || !generateTo || rules.length === 0}
                  className="flex-1 px-4 py-2 rounded-lg bg-app-accent text-black font-semibold text-sm disabled:opacity-50"
                >
                  {saving ? 'Generating...' : 'Generate Slots'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerCalendarScreen;
